// The mini build parses on every call. The full build caches each template
// and reuses the DOM nodes of its static subtrees on the next evaluation, so a
// second instance of a component would share elements with the first.
import htm from 'htm/mini';

function h(
  type: string,
  props: Record<string, any> | null,
  ...children: any[]
): HTMLElement | SVGElement {
  const isSvg = [
    'svg',
    'path',
    'rect',
    'circle',
    'line',
    'polyline',
    'polygon',
    'ellipse',
    'text',
    'tspan',
    'g',
    'defs',
    'mask',
    'linearGradient',
    'radialGradient',
    'stop',
  ].includes(type);

  const element = isSvg
    ? document.createElementNS('http://www.w3.org/2000/svg', type)
    : document.createElement(type);

  if (props) {
    for (const [key, value] of Object.entries(props)) {
      if (key === 'class' || key === 'className') {
        element.setAttribute('class', value);
      } else if (key === 'style' && typeof value === 'object') {
        Object.assign((element as HTMLElement).style, value);
      } else if (key.startsWith('on') && typeof value === 'function') {
        const event = key.slice(2).toLowerCase();
        element.addEventListener(event, value);
      } else if (value != null) {
        element.setAttribute(key, String(value));
      }
    }
  }

  for (const child of children.flat()) {
    // Booleans render nothing, as in JSX, so `${cond && html`…`}` leaves the
    // element out when cond is false.
    if (child == null || typeof child === 'boolean') continue;
    if (typeof child === 'string' || typeof child === 'number') {
      element.appendChild(document.createTextNode(String(child)));
    } else {
      element.appendChild(child);
    }
  }

  return element;
}

const build = htm.bind(h);

/**
 * Tagged template for creating DOM elements with JSX-like syntax.
 * Supports HTML and SVG elements with style objects and event handlers.
 * The template must have one root element, which it returns.
 * @example
 * html`<div style=${{ color: 'red' }} onClick=${handler}>Hello</div>`
 */
export function html(
  strings: TemplateStringsArray,
  ...values: unknown[]
): HTMLElement | SVGElement {
  const root = build(strings, ...values);
  if (!(root instanceof Element)) {
    throw new Error('html: the template must have one root element');
  }
  return root;
}

/** The element type of each ref, by its name in `data-ref`. */
type RefTypes = Record<string, abstract new () => Element>;

type Refs<T extends RefTypes> = { [K in keyof T]: InstanceType<T[K]> };

/**
 * The elements in `root` marked with `data-ref`, `root` included, by name.
 * Throws unless each name in `types` marks exactly one element, of that type,
 * so a misspelt or misplaced ref fails when the element is built rather than
 * when it is first animated.
 *
 * @example
 * const refs = collectRefs(
 *   html`<div data-ref="box"><span data-ref="text">Message</span></div>`,
 *   { box: HTMLDivElement, text: HTMLSpanElement }
 * );
 *
 * refs.text.textContent = 'Hello';
 * refs.box.style.color = 'red';
 */
export function collectRefs<T extends RefTypes>(
  root: Element,
  types: T
): Refs<T> {
  const marked = [root, ...root.querySelectorAll('[data-ref]')];
  const refs: Record<string, Element> = {};
  for (const [name, type] of Object.entries(types)) {
    const found = marked.filter((el) => el.getAttribute('data-ref') === name);
    const [element] = found;
    if (found.length !== 1 || !(element instanceof type)) {
      const what = found.map((el) => el.constructor.name).join(', ');
      throw new Error(
        `collectRefs: expected one ${type.name} with data-ref="${name}", found ${what || 'none'}`
      );
    }
    refs[name] = element;
  }
  return refs as Refs<T>;
}
